import {
  withCompInfo,
  createComponentMapperModel,
  withUiTypeCompInfo,
} from '@wix/editor-elements-integrations';
import type {
  PaginationDefinition,
  PaginationPropsCarmiData,
  PaginationCSSVars,
  PaginationCssCarmiData,
  PaginationModeProps,
  PaginationResponsiveUiType,
  IPaginationMapperProps,
} from '../Pagination.types';
import {
  alignmentCss,
  collapseAlignmentMap,
  currentPageDisplayedCss,
  currentPageVisuallyHiddenCss,
  justifiedAlignmentCss,
  PaginationAlignment,
} from './constants';
import { isResponsiveSkin } from './utils/mapperUtils';
import { AriaTranslationKeys } from '../constants';
import { isDisplayed } from '../editor/panels/utils';
import { getResponsivePaginationLayout } from '../utils';
import { castFromInheritIfNeeded } from '@wix/editor-elements-common-utils';

export const props = withCompInfo<
  IPaginationMapperProps,
  PaginationDefinition,
  PaginationPropsCarmiData
>()(
  [
    'compData',
    'compProps',
    'isMobileView',
    'isConnectedToDataBinding',
    'translate',
    'skin',
    'styleProperties',
    'directionWithOptInRtlSupport',
  ],
  (
    {
      compData,
      compProps,
      isMobileView,
      isConnectedToDataBinding,
      translate,
      skin,
      styleProperties,
      directionWithOptInRtlSupport,
    },
    carmiProps,
  ) => {
    const {
      totalPages,
      currentPage,
      nextText,
      previousText,
      firstText,
      lastText,
      direction = 'inherit',
      a11y,
      nextAriaLabel,
      previousAriaLabel,
      firstAriaLabel,
      lastAriaLabel,
    } = compData;
    const { navigationType } = carmiProps;
    const {
      showFirstLastNavButtons,
      showFirstPage,
      showLastPage,
      showInputModeTotalPages,
      paginationDirection,
      paginationMode,
      isDisabled,
      showPageNumbersBar,
    } = compProps;
    const {
      'button-label-display': buttonLabelDisplay,
      'button-icon-display': buttonIconDisplay,
    } = styleProperties;

    const paginationProps = {
      totalPages,
      currentPage,
      nextText,
      previousText,
      firstText,
      lastText,
      showFirstLastNavButtons,
      navigationType,
      paginationDirection,
      showPageNumbersBar,
      paginationMode,
      isDisabled,
      direction,
      a11y,
      nextAriaLabel,
      previousAriaLabel,
      firstAriaLabel,
      lastAriaLabel,
    };

    const paginationModeProps: PaginationModeProps = {
      input: { showTotalPages: showInputModeTotalPages },
      pages: {
        showFirstLastPageButtons: showFirstPage || showLastPage,
        isResponsiveHeight: isMobileView,
        maxPagesToShow: 100,
        showPageNumbersBar: true,
      },
      arrows: {
        isResponsiveHeight: isMobileView,
        maxPagesToShow: 0,
        showTotalPages: false,
        ...(isConnectedToDataBinding
          ? {
              showFirstLastPageButtons: false,
              showPageNumbersBar: false,
            }
          : {}),
      },
    };

    // In case of data binding, we want to force PaginationMode to be 'arrows'
    // TODO - this is temporarily removed due to https://jira.wixpress.com/browse/ECL-4649
    // const actualPaginationMode = isConnectedToDataBinding
    //   ? PaginationMode.Arrows
    //   : paginationMode;
    const actualPaginationMode = paginationMode;

    const ariaLabelTranslations = {
      firstPage:
        translate(
          AriaTranslationKeys.namespace,
          AriaTranslationKeys.firstPage,
        ) || 'First page',
      previousPage:
        translate(
          AriaTranslationKeys.namespace,
          AriaTranslationKeys.previousPage,
        ) || 'Previous page',
      nextPage:
        translate(
          AriaTranslationKeys.namespace,
          AriaTranslationKeys.nextPage,
        ) || 'Next page',
      lastPage:
        translate(
          AriaTranslationKeys.namespace,
          AriaTranslationKeys.lastPage,
        ) || 'Last page',
      currentPage:
        translate(
          AriaTranslationKeys.namespace,
          AriaTranslationKeys.currentPage,
        ) || 'Page {{currentPage}}',
      paginationLabel:
        translate(
          AriaTranslationKeys.namespace,
          AriaTranslationKeys.paginationLabel,
        ) || 'Pagination Bar',
    };

    const classicSkinProps = {
      ...paginationProps,
      ...paginationModeProps[actualPaginationMode],
      paginationModeProps,
      paginationMode: actualPaginationMode,
      ariaLabelTranslations: {
        ...ariaLabelTranslations,
        paginationInput:
          translate(
            AriaTranslationKeys.namespace,
            AriaTranslationKeys.paginationInput,
          ) || 'Select between 1 and <%= itemName %>.',
      },
    };

    const responsiveSkinProps = {
      totalPages,
      currentPage,
      nextText,
      previousText,
      firstText,
      lastText,
      isDisabled,
      direction: castFromInheritIfNeeded(
        direction,
        directionWithOptInRtlSupport,
      ),
      a11y,
      nextAriaLabel,
      previousAriaLabel,
      firstAriaLabel,
      lastAriaLabel,
      paginationLayout: getResponsivePaginationLayout(
        buttonLabelDisplay as any,
        buttonIconDisplay as any,
      ),
      ariaLabelTranslations,
    };

    return isResponsiveSkin(skin) ? responsiveSkinProps : classicSkinProps;
  },
);

export const css = withCompInfo<
  PaginationCSSVars | {},
  PaginationDefinition,
  PaginationCssCarmiData
>()(
  ['compProps', 'skin', 'styleProperties'],
  ({ compProps, skin, styleProperties }, cssVars) => {
    const { alignment } = compProps;
    const {
      'current-page-display': currentPageDisplay,
      'container-alignment': containerAlignment,
    } = styleProperties;
    const isAlignmentJustified = containerAlignment === 'space-between';
    const isCurrentPageDisplayed = isDisplayed(currentPageDisplay);

    const classicSkinCss = {
      ...cssVars,
      '--pagination-alignment':
        collapseAlignmentMap[alignment ?? PaginationAlignment.Center],
      'pagination-flex-grow': alignment ? 0 : 1,
    };

    const responsiveSkinCss = {
      ...(isCurrentPageDisplayed
        ? currentPageDisplayedCss
        : currentPageVisuallyHiddenCss),
      ...(isAlignmentJustified ? justifiedAlignmentCss : alignmentCss),
    };

    return isResponsiveSkin(skin) ? responsiveSkinCss : classicSkinCss;
  },
);

export const uiType = withUiTypeCompInfo<
  PaginationResponsiveUiType,
  PaginationDefinition
>()(['skin'], ({ skin }) => {
  return isResponsiveSkin(skin) ? skin : undefined;
});

export default createComponentMapperModel({ props, css, uiType });
